import { FileDoneOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Space } from "antd";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { message } from "shared/lib/antd-global";
import { createBatchSign, getBatchSignCounts } from "../api";
import { BatchSignType } from "../types";
import styles from "./index.module.scss";

const TYPES: BatchSignType[] = ["acts", "contracts", "tasks"];

/**
 * Пакетное подписание в eGov Mobile: по кнопке на каждый тип документов,
 * у которого есть что подписать. Счётчики обновляются при заходе на главную
 * и через pull-to-refresh.
 */
export const BatchSignBlock = () => {
  const { t } = useTranslation();

  const { data: counts, refetch } = useQuery({
    queryKey: ["batch-sign"],
    queryFn: getBatchSignCounts,
    staleTime: 0,
    gcTime: 0,
  });

  const {
    mutate: sign,
    isPending,
    variables: pendingType,
  } = useMutation({
    mutationFn: createBatchSign,
    onSuccess(response) {
      if (response?.success && response.link) {
        window.open(response.link, "_blank");
      } else {
        message.error(t("batch_sign.error"));
      }
    },
    onError(error: AxiosError<{ message?: string }>) {
      message.error(error.response?.data?.message || t("batch_sign.error"));
      // 422 — подписывать уже нечего: счётчик устарел.
      if (error.response?.status === 422) {
        refetch();
      }
    },
  });

  const available = TYPES.filter((type) => (counts?.[type] ?? 0) > 0);

  if (!available.length) {
    return null;
  }

  return (
    <div className={styles.block}>
      <div className={styles.iconWrapper}>
        <FileDoneOutlined />
      </div>
      <Space vertical size="middle" style={{ flex: 1, minWidth: 0 }}>
        {t("batch_sign.title")}
        <div className={styles.text}>{t("batch_sign.description")}</div>
        {available.map((type) => (
          <Button
            key={type}
            type="primary"
            block
            loading={isPending && pendingType === type}
            // Новый пакет отменяет ссылку предыдущего — не создаём параллельно.
            disabled={isPending && pendingType !== type}
            onClick={() => sign(type)}
          >
            {t(`batch_sign.sign_${type}`, { count: counts?.[type] })}
          </Button>
        ))}
      </Space>
    </div>
  );
};
